import {DefaultBoxGenerator} from '@gongxigames/framework/core/fragments/display/TextInput/DefaultBoxGenerator';
import {
    BoxCache,
    TextInputState,
    TextInputBoxGenerator,
    PreviousState,
    TextInputOptions,
    TextInputSelection,
    TextInputStyle, PaddingTuple, TextInputBox
} from '@gongxigames/framework/core/fragments/display/TextInput/textInputTypes';
import {
    Container,
    Graphics,
    Renderer,
    Text,
    TextStyle,
    TextStyleFontWeight,
    TextStyleFontVariant,
    TextStyleFontStyle,
    TextStyleAlign,
    TextMetrics
} from 'pixi.js';
import {DestroyOptions, IBrowser} from '@gongxigames/framework/core/types';
import {
    BoundsRect,
    getCanvasBounds,
    getWorldTransformCSSMatrix,
    isClientRectsEqual,
    isPixiMatricesEqual
} from '@gongxigames/framework/core/utils';

// this file cloned from https://github.com/Mwni/PIXI.TextInput
// and converted to TypeScript

interface IFontMetrics {
    ascent: number;
    descent: number;
    fontSize: number;
}

const DefaultInputStyle = {
    position: 'absolute',
    background: 'none',
    border: 'none',
    outline: 'none',
    transformOrigin: '0 0',
    lineHeight: '1'
};

const DEFAULT_PLACEHOLDER_COLOR = 0xa9a9a9;

export class TextInput extends Container {
    public state: TextInputState = TextInputState.DEFAULT;
    private readonly _input_style: TextInputStyle;
    private _box_generator: TextInputBoxGenerator | null = null;
    private readonly _multiline: boolean = false;
    private _box_cache: BoxCache = {
        [TextInputState.DEFAULT]: null,
        [TextInputState.DISABLED]: null,
        [TextInputState.FOCUSED]: null
    };
    private _previous: PreviousState = {};
    private _dom_added = false;
    private _dom_visible = true;
    private _placeholder = '';
    private _placeholderColor: number = DEFAULT_PLACEHOLDER_COLOR;
    private _selection: TextInputSelection = [0, 0];
    private _restrict_value = '';
    private _substituted = false;
    private _dom_input!: HTMLInputElement | HTMLTextAreaElement;
    private _disabled = false;
    private _restrict_regex: RegExp | null = null;
    private _last_renderer: Renderer | undefined;
    private _resolution = 1;
    private _canvas_bounds: BoundsRect | undefined;
    private _box: Graphics | null = null;
    private _surrogate: Text | null = null;
    private _surrogate_hitBox: Graphics | null = null;
    private _surrogate_mask: Graphics | null = null;
    private _font_metrics!: IFontMetrics;
    private _blurBind: () => void = this.blur.bind(this);
    private _browser: IBrowser;

    constructor(options: TextInputOptions, browser: IBrowser) {
        super();

        this.name = options.name ?? 'TextInput';

        this._browser = browser;
        this._input_style = Object.assign(DefaultInputStyle, options.input);

        if (options.box) {
            this._box_generator = typeof options.box === 'function' ? options.box : DefaultBoxGenerator(options.box);
        }

        if (this._input_style.hasOwnProperty('multiline')) {
            this._multiline = !!this._input_style.multiline;
            delete this._input_style.multiline;
        }

        this._createDOMInput();
        this.substituteText = true;
        this._setState(TextInputState.DEFAULT);
        this._addListeners();

        if (options.disabledPasteFromClipboard) {
            this._dom_input.onpaste = e => e.preventDefault();
        }

        if (options.input.maxLength) {
            this.maxLength = options.input.maxLength;
        }
    }

    public get substituteText(): boolean {
        return this._substituted;
    }

    public set substituteText(substitute: boolean) {
        if (this._substituted === substitute) {
            return;
        }

        this._substituted = substitute;

        if (substitute) {
            this._createSurrogate();
            this._dom_visible = false;
        } else {
            this._destroySurrogate();
            this._dom_visible = true;
        }

        this._update();
    }

    public get placeholder(): string {
        return this._placeholder;
    }

    public set placeholder(text: string) {
        this._placeholder = text;

        if (this._substituted) {
            this._updateSurrogate();
            this._dom_input.placeholder = '';
        } else {
            this._dom_input.placeholder = text;
        }
    }

    public get disabled(): boolean {
        return this._disabled;
    }

    public set disabled(disabled: boolean) {
        this._disabled = disabled;
        this._dom_input.disabled = disabled;
        this._setState(disabled ? TextInputState.DISABLED : TextInputState.DEFAULT);
    }

    public get maxLength(): number {
        return Number(this._dom_input.getAttribute('maxlength'));
    }

    public set maxLength(length: number) {
        this._dom_input.setAttribute('maxlength', length.toString());
    }

    public set restrict(regex: RegExp | string) {
        if (regex instanceof RegExp) {
            let regexString = regex.toString().slice(1, -1);

            if (regexString.charAt(0) !== '^') {
                regexString = `^${regexString}`;
            }

            if (regexString.charAt(regexString.length - 1) !== '$') {
                regexString = `${regexString}$`;
            }

            regex = new RegExp(regexString);
        } else {
            regex = new RegExp(`^[${regex}]*$`);
        }

        this._restrict_regex = regex;
    }

    public get text(): string {
        return this._dom_input.value;
    }

    public set text(text: string) {
        if (this._dom_input.value === text) {
            return;
        }

        this._dom_input.value = text;

        if (this._substituted) {
            this._updateSurrogate();
        }
    }

    public focus(): void {
        if (this._substituted && !this._dom_visible) {
            this._setDOMInputVisible(true);
        }

        this._dom_input.focus();
    }

    public blur(): void {
        this._dom_input.blur();
    }

    public select(): void {
        this.focus();
        this._dom_input.select();
    }

    public setBoxStyle(box?: TextInputBox | TextInputBoxGenerator): void {
        if (box) {
            this._box_generator = typeof box === 'function' ? box : DefaultBoxGenerator(box);
        } else {
            this._box_generator = null;
        }

        this._buildBoxCache();
        this._updateBox();
    }

    public setInputStyle<K extends keyof TextInputStyle>(key: K, value: TextInputStyle[K]): void {
        this._input_style[key] = value;
        this._dom_input.style[key as any] = value as string;

        if (this._substituted && (key === 'fontFamily' || key === 'fontSize')) {
            this._updateFontMetrics();
        }

        if (this._last_renderer) {
            this._update();
        }
    }

    public destroy(options?: DestroyOptions): void {
        this._destroyBoxCache();
        super.destroy(options);
    }

    public render(renderer: Renderer): void {
        super.render(renderer);
        this._renderInternal(renderer);
    }

    public addRequiredListeners(): void {
        this.on('added', this._onAdded.bind(this));
        this.on('removed', this._onRemoved.bind(this));
    }

    private _createDOMInput(): void {
        if (this._multiline) {
            this._dom_input = document.createElement('textarea');
            this._dom_input.style.resize = 'none';
        } else {
            this._dom_input = document.createElement('input');
            this._dom_input.type = 'text';
        }

        for (const key in this._input_style) {
            if (this._input_style.hasOwnProperty(key)) {
                this._dom_input.style[key as any] = this._input_style[key as keyof TextInputStyle] as any;
            }
        }
    }

    private _addListeners(): void {
        this.addRequiredListeners();

        this._dom_input.addEventListener('keydown', this._onInputKeyDown.bind(this));
        this._dom_input.addEventListener('input', this._onInputInput.bind(this));
        this._dom_input.addEventListener('keyup', this._onInputKeyUp.bind(this));
        this._dom_input.addEventListener('focus', this._onFocused.bind(this));
        this._dom_input.addEventListener('blur', this._onBlurred.bind(this));

        document.body.addEventListener('pointerdown', this._blurBind);
    }

    private _removeListeners(): void {
        document.body.removeEventListener('pointerdown', this._blurBind);
    }

    private _onInputKeyDown(e: any): void {
        if (!this.parent) {
            return;
        }

        this._selection = [
            this._dom_input.selectionStart,
            this._dom_input.selectionEnd
        ];

        this.emit('keydown', e.keyCode);
    }

    private _onInputInput(): void {
        if (!this.parent) {
            return;
        }

        if (this._restrict_regex) {
            this._applyRestriction();
        }

        if (this._substituted) {
            this._updateSubstitution();
        }

        this.emit('input', this.text);
    }

    private _onInputKeyUp(e: any): void {
        if (!this.parent) {
            return;
        }

        this.emit('keyup', e.keyCode);
    }

    private _onFocused(): void {
        if (!this.parent) {
            return;
        }

        if (this.state !== TextInputState.FOCUSED) {
            this._setState(TextInputState.FOCUSED);
            this.emit('focus');
        }
    }

    private _onBlurred(): void {
        if (!this.parent) {
            return;
        }

        if (this.state === TextInputState.FOCUSED) {
            this._setState(TextInputState.DEFAULT);
            this.emit('blur');
        }
    }

    private _onAdded(): void {
        document.body.appendChild(this._dom_input);
        this._dom_input.style.display = 'none';
        this._dom_added = true;
    }

    private _onRemoved(): void {
        this._removeListeners();
        document.body.removeChild(this._dom_input);
        this._dom_added = false;
    }

    private _setState(state: TextInputState): void {
        this.state = state;
        this._updateBox();

        if (this._substituted) {
            this._updateSubstitution();
        }
    }

    private _renderInternal(renderer: Renderer): void {
        this._resolution = renderer.resolution;
        this._last_renderer = renderer;
        this._canvas_bounds = getCanvasBounds(renderer);

        if (this._needsUpdate()) {
            this._update();
        }
    }

    private _update(): void {
        this._updateDOMInput();

        if (this._substituted) {
            this._updateSurrogate();
        }

        this._updateBox();
    }

    private _updateBox(): void {
        if (!this._box_generator) {
            return;
        }

        if (this._needsNewBoxCache()) {
            this._buildBoxCache();
        }

        if (this.state === this._previous.state && this._box === this._box_cache[this.state]) {
            return;
        }

        if (this._box) {
            this.removeChild(this._box);
        }

        this._box = this._box_cache[this.state];

        if (this._box) {
            this.addChildAt(this._box, 0);
        }

        this._previous.state = this.state;
    }

    private _updateSubstitution(): void {
        if (this.state === TextInputState.FOCUSED) {
            this._dom_visible = true;

            if (this._surrogate) {
                this._surrogate.visible = this.text.length === 0;
            }
        } else {
            this._dom_visible = false;

            if (this._surrogate) {
                this._surrogate.visible = true;
            }
        }
        this._updateDOMInput();
        this._updateSurrogate();
    }

    private _updateDOMInput(): void {
        if (!this._canvas_bounds) {
            return;
        }

        this._dom_input.style.top = `${this._canvas_bounds.top || 0}px`;
        this._dom_input.style.left = `${this._canvas_bounds.left || 0}px`;

        if (this._last_renderer) {
            this._dom_input.style.transform = getWorldTransformCSSMatrix(this._last_renderer, this.worldTransform, this._resolution);
        }

        this._dom_input.style.opacity = this.worldAlpha.toString();

        this._setDOMInputVisible(this.worldVisible && this._dom_visible);

        this._previous.canvas_bounds = this._canvas_bounds;
        this._previous.world_transform = this.worldTransform.clone();
        this._previous.world_alpha = this.worldAlpha;
        this._previous.world_visible = this.worldVisible;
    }

    private _applyRestriction(): void {
        if (this._restrict_regex && this._restrict_regex.test(this.text)) {
            this._restrict_value = this.text;
        } else {
            this.text = this._restrict_value;
            this._dom_input.setSelectionRange(
                this._selection[0],
                this._selection[1]
            );
        }
    }

    private _needsUpdate(): boolean {
        return (
            !isPixiMatricesEqual(this.worldTransform, this._previous.world_transform)
            || !isClientRectsEqual(this._canvas_bounds, this._previous.canvas_bounds)
            || this.worldAlpha !== this._previous.world_alpha
            || this.worldVisible !== this._previous.world_visible
        );
    }

    private _needsNewBoxCache(): boolean {
        const inputBounds = this._getDOMInputBounds();

        return (
            !this._previous.input_bounds
            || inputBounds.width !== this._previous.input_bounds.width
            || inputBounds.height !== this._previous.input_bounds.height
        );
    }

    private _createSurrogate(): void {
        this._surrogate_hitBox = new Graphics();
        this._surrogate_hitBox.alpha = 0;
        this._surrogate_hitBox.interactive = true;
        this._surrogate_hitBox.cursor = 'text';
        this._surrogate_hitBox.on('pointerdown', this._onSurrogateFocus.bind(this));
        this.addChild(this._surrogate_hitBox);

        this._surrogate_mask = new Graphics();
        this.addChild(this._surrogate_mask);

        this._surrogate = new Text('');
        this._surrogate.resolution = 1;

        this.addChild(this._surrogate);

        this._surrogate.mask = this._surrogate_mask;

        this._updateFontMetrics();
        this._updateSurrogate();
    }

    private _updateSurrogate(): void {
        if (this._surrogate) {
            const padding = this._deriveSurrogatePadding();
            const inputBounds = this._getDOMInputBounds();

            this._surrogate.style = this._deriveSurrogateStyle();
            this._surrogate.style.padding = Math.max.apply(Math, padding);
            this._surrogate.y = this._multiline ? padding[0] : (inputBounds.height - this._surrogate.height) / 2;
            this._surrogate.x = padding[3];
            this._surrogate.text = this._deriveSurrogateText();

            switch (this._surrogate.style.align) {
                default:
                case 'left':
                    this._surrogate.x = padding[3];
                    break;

                case 'center':
                    this._surrogate.x = inputBounds.width * 0.5 - this._surrogate.width * 0.5;
                    break;

                case 'right':
                    this._surrogate.x = inputBounds.width - padding[1] - this._surrogate.width;
                    break;
            }

            this._updateSurrogateHitBox(inputBounds);
            this._updateSurrogateMask(inputBounds, padding);
        }
    }

    private _updateSurrogateHitBox(bounds: BoundsRect): void {
        if (this._surrogate_hitBox) {
            this._surrogate_hitBox.clear();
            this._surrogate_hitBox.beginFill(0);
            this._surrogate_hitBox.drawRect(0, 0, bounds.width, bounds.height);
            this._surrogate_hitBox.endFill();
            this._surrogate_hitBox.interactive = !this._disabled;
        }
    }

    private _updateSurrogateMask(bounds: BoundsRect, padding: PaddingTuple): void {
        if (this._surrogate_mask) {
            this._surrogate_mask.clear();
            this._surrogate_mask.beginFill(0);
            this._surrogate_mask.drawRect(padding[3], 0, bounds.width - padding[3] - padding[1], bounds.height);
            this._surrogate_mask.endFill();
        }
    }

    private _destroySurrogate(): void {
        if (!this._surrogate) {
            return;
        }

        this.removeChild(this._surrogate);
        this._surrogate.destroy();

        if (this._surrogate_hitBox) {
            this.removeChild(this._surrogate_hitBox);
            this._surrogate_hitBox.destroy();
        }

        this._surrogate = null;
        this._surrogate_hitBox = null;
    }

    private _onSurrogateFocus(): void {
        this._setDOMInputVisible(true);
        this._ensureFocus();
        
        // sometimes the input is not being focused by the mouseclick
        this._browser.addTimeout(this._ensureFocus.bind(this), 10);
    }

    private _ensureFocus(): void {
        if (!this._hasFocus()) {
            this.focus();
        }
    }

    private _deriveSurrogateStyle(): TextStyle {
        const style = new TextStyle();

        for (const key in this._input_style) {
            if (!this._input_style.hasOwnProperty(key) || this._input_style[key as keyof TextInputStyle] === undefined) {
                continue;
            }

            const value = this._input_style[key as keyof TextInputStyle] as string;

            switch (key) {
                case 'color':
                    style.fill = value;
                    break;

                case 'fontFamily':
                case 'fontSize':
                    style[key] = value;
                    break;
                case 'fontWeight':
                    style[key] = value as TextStyleFontWeight;
                    break;
                case 'fontVariant':
                    style[key] = value as TextStyleFontVariant;
                    break;
                case 'fontStyle':
                    style[key] = value as TextStyleFontStyle;
                    break;
                case 'letterSpacing':
                    style.letterSpacing = parseFloat(value);
                    break;
                case 'textAlign':
                    style.align = value as TextStyleAlign;
                    break;
                default:
                    break;
            }
        }

        if (this._multiline) {
            if (typeof style.fontSize === 'string') {
                style.lineHeight = parseFloat(style.fontSize);
            }

            style.wordWrap = true;
            style.wordWrapWidth = this._getDOMInputBounds().width;
        }

        if (this._dom_input.value.length === 0) {
            style.fill = this._placeholderColor;
        }

        return style;
    }

    private _deriveSurrogatePadding(): PaddingTuple {
        const {textIndent} = this._input_style;
        const indent = textIndent && Number(textIndent) ? parseFloat(textIndent) : 0;

        if (this._input_style.padding && this._input_style.padding.length > 0) {
            const components = this._input_style.padding.trim().split(' ').map(parseFloat);

            if (components.length === 1) {
                const padding = components[0];

                return [padding, padding, padding, padding + indent];
            } else if (components.length === 2) {
                const paddingV = components[0];
                const paddingH = components[1];

                return [paddingV, paddingH, paddingV, paddingH + indent];
            } else if (components.length === 4) {
                const padding = components;

                padding[3] += indent;

                return padding as [number, number, number, number];
            }
        }

        return [0, 0, 0, indent];
    }

    private _deriveSurrogateText(): string {
        if (this._dom_input.value.length === 0) {
            return this._placeholder;
        }

        if (this._dom_input.type === 'password') {
            return '•'.repeat(this._dom_input.value.length);
        }

        return this._dom_input.value;
    }

    private _updateFontMetrics(): void {
        const style = this._deriveSurrogateStyle();
        const font = style.toFontString();

        this._font_metrics = TextMetrics.measureFont(font);
    }

    private _buildBoxCache(): void {
        this._destroyBoxCache();

        const states = [TextInputState.DEFAULT, TextInputState.FOCUSED, TextInputState.DISABLED];
        const inputBounds = this._getDOMInputBounds();

        states.forEach(state => {
            if (this._box_generator !== null) {
                this._box_cache[state] = this._box_generator(inputBounds.width, inputBounds.height, state);
            }
        });

        this._previous.input_bounds = inputBounds;
    }

    private _destroyBoxCache(): void {
        if (this._box) {
            this.removeChild(this._box);
            this._box = null;
        }

        for (const key in this._box_cache) {
            if (this._box_cache.hasOwnProperty(key)) {
                const box = this._box_cache[key as TextInputState];

                if (box && box.geometry) {
                    box.destroy();
                }

                this._box_cache[key as TextInputState] = null;
            }

        }
    }

    private _hasFocus(): boolean {
        return document.activeElement === this._dom_input;
    }

    private _setDOMInputVisible(visible: boolean): void {
        this._dom_input.style.display = visible ? 'block' : 'none';
    }

    private _getDOMInputBounds(): DOMRect {
        let removeAfter = false;

        if (!this._dom_added) {
            document.body.appendChild(this._dom_input);
            removeAfter = true;
        }

        const orgTransform = this._dom_input.style.transform;
        const orgDisplay = this._dom_input.style.display;

        this._dom_input.style.transform = '';
        this._dom_input.style.display = 'block';

        const bounds = this._dom_input.getBoundingClientRect();

        this._dom_input.style.transform = orgTransform;
        this._dom_input.style.display = orgDisplay;

        if (removeAfter) {
            document.body.removeChild(this._dom_input);
        }

        return bounds;
    }
}
